'use client';

import { useState } from 'react';
import { PDFDocument, StandardFonts, rgb, type PDFPage, type PDFFont } from 'pdf-lib';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WD_SUN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WD_MON = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const INK = rgb(0.09, 0.1, 0.14);
const MUTED = rgb(0.36, 0.38, 0.45);
const LINE = rgb(0.84, 0.86, 0.9);
const ACCENT = rgb(0.29, 0.31, 0.86);

type Fonts = { font: PDFFont; bold: PDFFont };
type Scope = 'month' | 'year' | 'overview';
type Paper = 'letter' | 'a4';
type Orientation = 'portrait' | 'landscape';

const PAPER: Record<Paper, [number, number]> = { letter: [612, 792], a4: [595.28, 841.89] };

function triggerDownload(bytes: Uint8Array, name: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function monthCells(year: number, month: number, mondayStart: boolean): (number | null)[] {
  const first = new Date(year, month, 1).getDay(); // 0 = Sunday
  const days = new Date(year, month + 1, 0).getDate();
  const offset = mondayStart ? (first + 6) % 7 : first;
  const cells: (number | null)[] = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// Draw a full month grid. topY is the y-coordinate of the top of the grid (weekday row).
function drawMonthGrid(
  page: PDFPage,
  f: Fonts,
  year: number,
  month: number,
  mondayStart: boolean,
  left: number,
  topY: number,
  width: number,
  height: number,
) {
  const labels = mondayStart ? WD_MON : WD_SUN;
  const cells = monthCells(year, month, mondayStart);
  const weeks = cells.length / 7;
  const colW = width / 7;
  const headerH = 20;
  const rowH = (height - headerH) / weeks;

  // weekday header
  for (let c = 0; c < 7; c++) {
    const cx = left + c * colW;
    page.drawText(labels[c], {
      x: cx + 6,
      y: topY - 14,
      size: 9,
      font: f.bold,
      color: MUTED,
    });
  }
  const gridTop = topY - headerH;
  // horizontal lines
  for (let r = 0; r <= weeks; r++) {
    const y = gridTop - r * rowH;
    page.drawLine({
      start: { x: left, y },
      end: { x: left + width, y },
      thickness: r === 0 ? 1.2 : 0.6,
      color: r === 0 ? INK : LINE,
    });
  }
  // vertical lines
  for (let c = 0; c <= 7; c++) {
    const x = left + c * colW;
    page.drawLine({
      start: { x, y: gridTop },
      end: { x, y: gridTop - weeks * rowH },
      thickness: 0.6,
      color: LINE,
    });
  }
  // day numbers
  cells.forEach((d, i) => {
    if (d == null) return;
    const r = Math.floor(i / 7);
    const c = i % 7;
    const x = left + c * colW + 6;
    const y = gridTop - r * rowH - 14;
    page.drawText(String(d), { x, y, size: 11, font: f.bold, color: INK });
  });
}

function drawMonthPage(
  pdf: PDFDocument,
  f: Fonts,
  year: number,
  month: number,
  mondayStart: boolean,
  w: number,
  h: number,
) {
  const page = pdf.addPage([w, h]);
  const m = 40;
  page.drawText(`${MONTHS[month]} ${year}`, {
    x: m,
    y: h - m - 10,
    size: 26,
    font: f.bold,
    color: INK,
  });
  page.drawLine({
    start: { x: m, y: h - m - 22 },
    end: { x: w - m, y: h - m - 22 },
    thickness: 2,
    color: ACCENT,
  });
  drawMonthGrid(page, f, year, month, mondayStart, m, h - m - 46, w - 2 * m, h - m - 46 - m);
}

function drawMiniMonth(
  page: PDFPage,
  f: Fonts,
  year: number,
  month: number,
  mondayStart: boolean,
  left: number,
  topY: number,
  width: number,
  height: number,
) {
  const labels = mondayStart ? WD_MON : WD_SUN;
  const cells = monthCells(year, month, mondayStart);
  const weeks = cells.length / 7;
  const colW = width / 7;
  page.drawText(`${MONTHS[month]}`, { x: left, y: topY, size: 11, font: f.bold, color: ACCENT });
  const headTop = topY - 14;
  for (let c = 0; c < 7; c++) {
    page.drawText(labels[c][0], {
      x: left + c * colW + colW / 2 - 2.5,
      y: headTop,
      size: 6.5,
      font: f.bold,
      color: MUTED,
    });
  }
  const gridTop = headTop - 9;
  const rowH = (height - 24) / weeks;
  cells.forEach((d, i) => {
    if (d == null) return;
    const r = Math.floor(i / 7);
    const c = i % 7;
    const s = String(d);
    const tw = f.font.widthOfTextAtSize(s, 6.8);
    page.drawText(s, {
      x: left + c * colW + colW / 2 - tw / 2,
      y: gridTop - r * rowH - 6,
      size: 6.8,
      font: f.font,
      color: INK,
    });
  });
}

function drawOverviewPage(
  pdf: PDFDocument,
  f: Fonts,
  year: number,
  mondayStart: boolean,
  w: number,
  h: number,
) {
  const page = pdf.addPage([w, h]);
  const m = 36;
  page.drawText(`${year}`, { x: m, y: h - m - 4, size: 30, font: f.bold, color: INK });
  page.drawLine({
    start: { x: m, y: h - m - 16 },
    end: { x: w - m, y: h - m - 16 },
    thickness: 2,
    color: ACCENT,
  });
  const cols = w >= h ? 4 : 3;
  const rows = 12 / cols;
  const gapX = 22;
  const gapY = 18;
  const areaTop = h - m - 40;
  const cellW = (w - 2 * m - (cols - 1) * gapX) / cols;
  const cellH = (areaTop - m - (rows - 1) * gapY) / rows;
  for (let month = 0; month < 12; month++) {
    const col = month % cols;
    const row = Math.floor(month / cols);
    const left = m + col * (cellW + gapX);
    const top = areaTop - row * (cellH + gapY);
    drawMiniMonth(page, f, year, month, mondayStart, left, top, cellW, cellH);
  }
}

export default function BrowserPrintableCalendar() {
  const [year, setYear] = useState<number>(() => {
    const y = new Date().getFullYear();
    return Number.isFinite(y) ? y + 1 : 2026;
  });
  const [scope, setScope] = useState<Scope>('year');
  const [month, setMonth] = useState<number>(0);
  const [mondayStart, setMondayStart] = useState<boolean>(false);
  const [paper, setPaper] = useState<Paper>('letter');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      if (!Number.isInteger(year) || year < 1 || year > 4000) {
        throw new Error('Enter a valid year.');
      }
      const pdf = await PDFDocument.create();
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
      const f: Fonts = { font, bold };
      let [w, h] = PAPER[paper];
      if (orientation === 'landscape') [w, h] = [h, w];

      if (scope === 'month') {
        drawMonthPage(pdf, f, year, month, mondayStart, w, h);
      } else if (scope === 'overview') {
        drawOverviewPage(pdf, f, year, mondayStart, w, h);
      } else {
        for (let mo = 0; mo < 12; mo++) drawMonthPage(pdf, f, year, mo, mondayStart, w, h);
      }
      pdf.setTitle(`${year} Calendar`);
      pdf.setProducer('Cleanor · cleanor.app');
      const bytes = Uint8Array.from(await pdf.save());
      const name =
        scope === 'month'
          ? `cleanor.app-${MONTHS[month].toLowerCase()}-${year}-calendar.pdf`
          : `cleanor.app-${year}-calendar.pdf`;
      triggerDownload(bytes, name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate the calendar.');
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--muted, #5b6072)',
  };

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Printable calendar</h2>
          <p>
            Pick a year and layout, then download a print-ready PDF. Everything is built in your
            browser, so nothing is uploaded.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div
        className="tool-live-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 16,
          marginTop: 8,
        }}
      >
        <label style={fieldStyle}>
          <span style={labelStyle}>Year</span>
          <input
            type="number"
            value={year}
            min={1}
            max={4000}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="tool-live-input"
            style={{ padding: '8px 10px' }}
          />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Layout</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
            style={{ padding: '8px 10px' }}
          >
            <option value="year">Full year (12 pages)</option>
            <option value="overview">Year on one page</option>
            <option value="month">Single month</option>
          </select>
        </label>
        {scope === 'month' ? (
          <label style={fieldStyle}>
            <span style={labelStyle}>Month</span>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              style={{ padding: '8px 10px' }}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label style={fieldStyle}>
          <span style={labelStyle}>Week starts</span>
          <select
            value={mondayStart ? 'mon' : 'sun'}
            onChange={(e) => setMondayStart(e.target.value === 'mon')}
            style={{ padding: '8px 10px' }}
          >
            <option value="sun">Sunday</option>
            <option value="mon">Monday</option>
          </select>
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Paper</span>
          <select
            value={paper}
            onChange={(e) => setPaper(e.target.value as Paper)}
            style={{ padding: '8px 10px' }}
          >
            <option value="letter">US Letter</option>
            <option value="a4">A4</option>
          </select>
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Orientation</span>
          <select
            value={orientation}
            onChange={(e) => setOrientation(e.target.value as Orientation)}
            style={{ padding: '8px 10px' }}
          >
            <option value="portrait">Portrait</option>
            <option value="landscape">Landscape</option>
          </select>
        </label>
      </div>

      <div className="hero-actions tool-live-actions" style={{ marginTop: 18 }}>
        <button className="button" type="button" onClick={generate} disabled={busy}>
          {busy ? 'Building PDF…' : 'Download calendar PDF'}
        </button>
      </div>
      {error ? <p className="support-feedback support-feedback-error">{error}</p> : null}
    </article>
  );
}
